<script setup lang="ts">
import { ref, onMounted, onBeforeMount, nextTick, computed } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

const { t } = useI18n();
const emit = defineEmits(["close", "capture"]);

const video = ref<HTMLVideoElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const image = ref<string | null>(null);
const stream = ref<MediaStream | null>(null);
const loading = ref(false);

// Direção da câmera: 'user' (frontal) ou 'environment' (traseira)
const cameraFacingMode = ref<"user" | "environment">("environment");

const isMobile = computed(() => store.getters.mobile);

function imageCapture() {
  if (!canvas.value || !video.value) return;

  const context = canvas.value.getContext("2d");
  canvas.value.width = video.value.videoWidth;
  canvas.value.height = video.value.videoHeight;

  context?.drawImage(
    video.value,
    0,
    0,
    canvas.value.width,
    canvas.value.height
  );

  image.value = canvas.value.toDataURL("image/png");

  closeCamera();

  setTimeout(() => {
    emit("capture", image.value);
  }, 100);
}

async function initCamera() {
  if (!video.value || !canvas.value) return;

  try {
    loading.value = true;

    if (stream.value) {
      stream.value.getTracks().forEach((track) => track.stop());
    }

    stream.value = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: cameraFacingMode.value,
      },
      audio: false,
    });

    video.value.srcObject = stream.value;
    loading.value = false;
  } catch (error) {
    console.error("Erro ao acessar a câmera:", error);
    loading.value = false;
  }
}

function switchCamera() {
  cameraFacingMode.value =
    cameraFacingMode.value === "user" ? "environment" : "user";
  initCamera();
}

function closeCamera() {
  if (stream.value) {
    stream.value.getTracks().forEach((track) => track.stop());
    stream.value = null;
  }

  // Só limpe as refs depois de parar o stream
  if (video.value) {
    video.value.srcObject = null;
  }
}

function cancel() {
  image.value = null;
  closeCamera();
  emit("close");
}

onBeforeMount(() => {
  closeCamera();
});

onMounted(async () => {
  await nextTick();
  initCamera();
});
</script>

<template>
  <section class="base-camera">
    <div class="content">
      <video
        ref="video"
        autoplay
        playsinline
        muted
        :style="{ opacity: image ? 0 : 1 }"
      ></video>

      <div class="actions">
        <button
          v-if="isMobile"
          type="button"
          class="btn btn-md btn-secondary px-4"
          @click="switchCamera"
        >
          {{ t("common.labels.switch_camera") }}
        </button>

        <button
          type="button"
          class="btn btn-md btn-primary px-4"
          @click="imageCapture"
        >
          {{ t("common.labels.capture") }}
        </button>

        <button
          type="button"
          class="btn btn-md btn-default px-4"
          @click="cancel"
        >
          {{ t("common.labels.cancel") }}
        </button>
      </div>
    </div>

    <canvas ref="canvas" style="display: none"></canvas>
  </section>
</template>

<style lang="scss" scoped>
.base-camera {
  position: absolute;
  z-index: 99;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100dvw;
  height: 100dvh;
  background: var(--black-transparent);

  .content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 1rem;
    min-width: 500px;

    .actions {
      display: flex;
      flex-direction: row;
      gap: 1.5rem;
      padding-block-start: 1rem;
    }

    video {
      width: 100%;
      max-width: 500px;
      border: 2px solid #333;
    }

    img {
      margin-top: 20px;
      width: 100%;
      max-width: 500px;
    }
  }
}

@media screen and (max-width: 1024px) {
  .base-camera {
    .content {
      width: 80vw;
      max-width: 80vw;
      min-width: 80vw;

      .actions {
        flex-direction: row;

        button {
          width: fit-content;
          font-size: 0.75rem;
        }
      }
    }
  }
  
}
</style>
